import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import classes from './Select.module.css';
// import { Link } from 'react-router-dom';

const Select = ({ onSelected = s => s, defaultValue, style = {}, selectedOption, disabled = false, options, pageContent, ...props }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [disabledTooltip, setDisabledTooltip] = useState(null);
    const [menuLayout, setMenuLayout] = useState(null);
    const tooltipRootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuPortalRef = useRef(null);

    const toggling = () => setIsOpen((open) => !open);

    const onOptionClicked = option => () => {
        setIsOpen(false);
        onSelected(option);
    };

    const createMarkup = (str) => ({ __html: str });

    const showDisabledTooltip = useCallback((e) => {
        if (!disabled || !pageContent?.register_to_change_lang) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const vw = window.innerWidth;
        const margin = 12;
        const maxW = Math.min(420, vw - margin * 2);
        let left = rect.left;
        if (left + maxW > vw - margin) left = Math.max(margin, vw - margin - maxW);
        if (left < margin) left = margin;
        setDisabledTooltip({ top: rect.bottom + 8, left, maxWidth: maxW });
    }, [disabled, pageContent?.register_to_change_lang]);

    const hideDisabledTooltip = useCallback(() => {
        setDisabledTooltip(null);
    }, []);

    useEffect(() => {
        if (!isOpen) setDisabledTooltip(null);
    }, [isOpen]);

    const updateMenuLayout = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const gap = 4;
        const margin = 8;
        const maxListH = Math.min(window.innerHeight * 0.7, 320);
        let top = r.bottom + gap;
        let maxH = maxListH;
        const spaceBelow = window.innerHeight - margin - top;
        if (spaceBelow < 120 && r.top - margin > window.innerHeight - r.bottom - margin) {
            maxH = Math.min(maxListH, Math.max(120, r.top - margin - gap));
            top = Math.max(margin, r.top - gap - maxH);
        } else {
            maxH = Math.min(maxListH, Math.max(80, spaceBelow));
        }
        let left = r.left;
        let width = r.width;
        const vw = window.innerWidth;
        const maxR = vw - margin;
        if (left + width > maxR) {
            left = Math.max(margin, maxR - width);
        }
        if (left < margin) {
            left = margin;
            width = Math.min(width, maxR - margin);
        }
        setMenuLayout({
            top,
            left,
            width,
            maxHeight: maxH,
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) {
            setMenuLayout(null);
            return;
        }
        updateMenuLayout();
        window.addEventListener('resize', updateMenuLayout);
        window.addEventListener('scroll', updateMenuLayout, true);
        return () => {
            window.removeEventListener('resize', updateMenuLayout);
            window.removeEventListener('scroll', updateMenuLayout, true);
        };
    }, [isOpen, updateMenuLayout]);

    useEffect(() => {
        if (!isOpen) return;
        const closeIfOutside = (e) => {
            const t = e.target;
            if (triggerRef.current?.contains(t)) return;
            if (menuPortalRef.current?.contains(t)) return;
            setIsOpen(false);
        };
        document.addEventListener('pointerdown', closeIfOutside, true);
        return () => document.removeEventListener('pointerdown', closeIfOutside, true);
    }, [isOpen]);

    useEffect(() => {
        if (!disabledTooltip) return;
        const hide = () => setDisabledTooltip(null);
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        return () => {
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [disabledTooltip]);

    // Aplica user-select:none !important a todos los nodos del HTML inyectado,
    // incluyendo inline styles que pueda traer el CMS.
    useLayoutEffect(() => {
        const root = tooltipRootRef.current;
        if (!root) return;
        const applyNoSelect = (el) => {
            if (el.nodeType !== Node.ELEMENT_NODE) return;
            el.style.setProperty('user-select', 'none', 'important');
            el.style.setProperty('-webkit-user-select', 'none', 'important');
            el.style.setProperty('-moz-user-select', 'none', 'important');
            el.style.setProperty('-ms-user-select', 'none', 'important');
        };
        applyNoSelect(root);
        root.querySelectorAll('*').forEach(applyNoSelect);
    }, [disabledTooltip, pageContent?.register_to_change_lang]);

    // Bloquea selección y copia cuando el tooltip está visible.
    useEffect(() => {
        const root = tooltipRootRef.current;
        if (!root || !disabledTooltip) return;

        // Comprueba si algún rango de la selección actual toca el nodo root
        const selectionTouchesTooltip = () => {
            const sel = document.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
            for (let i = 0; i < sel.rangeCount; i++) {
                if (sel.getRangeAt(i).intersectsNode(root)) return true;
            }
            return false;
        };

        // Impide empezar una selección desde dentro del tooltip
        const onSelectStart = (e) => {
            if (root.contains(e.target)) e.preventDefault();
        };

        // Borra la selección en cuanto incluye cualquier parte del tooltip
        const onSelectionChange = () => {
            if (selectionTouchesTooltip()) document.getSelection().removeAllRanges();
        };

        // Bloquea copiar/cortar si la selección toca el tooltip
        const onCopyOrCut = (e) => {
            if (selectionTouchesTooltip()) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        document.addEventListener('selectstart', onSelectStart, true);
        document.addEventListener('selectionchange', onSelectionChange);
        document.addEventListener('copy', onCopyOrCut, true);
        document.addEventListener('cut', onCopyOrCut, true);

        return () => {
            document.removeEventListener('selectstart', onSelectStart, true);
            document.removeEventListener('selectionchange', onSelectionChange);
            document.removeEventListener('copy', onCopyOrCut, true);
            document.removeEventListener('cut', onCopyOrCut, true);
        };
    }, [disabledTooltip, pageContent?.register_to_change_lang]);

    return (
        <>
            <div className={classes.dropDownContainer} {...props}>
                <div ref={triggerRef} style={style} className={classes.dropDownHeader} onClick={toggling}>
                    <div style={{ padding: '0 5px', fontFamily: "'Roboto', sans-serif", fontWeight: '400' }}>
                        {selectedOption || defaultValue}
                    </div>
                    <div style={{ padding: '0 5px' }}>
                        <svg width="16" height="8" viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M-2.40348e-07 0.645339C-2.34636e-07 0.514648 0.0318497 0.383945 0.106151 0.266323C0.275982 -0.0211985 0.615642 -0.0865649 0.84916 0.122541L8.0033 6.52646L15.1575 0.122542C15.391 -0.0865643 15.7306 -0.0211978 15.9005 0.266323C16.0703 0.553845 16.0172 0.972073 15.7837 1.18118L8.31113 7.87258C8.13068 8.04247 7.87594 8.04247 7.68488 7.87258L0.222894 1.18118C0.0849064 1.05049 -2.49488e-07 0.854418 -2.40919e-07 0.658381L-2.40348e-07 0.645339Z" fill="#B66A00" />
                        </svg>
                    </div>
                </div>
            </div>
            {isOpen &&
                menuLayout &&
                createPortal(
                    <div
                        ref={menuPortalRef}
                        data-app-select-menu="true"
                        className={classes.dropDownListPortal}
                        style={{
                            top: menuLayout.top,
                            left: menuLayout.left,
                            width: menuLayout.width,
                        }}
                    >
                        <ul
                            className={classes.dropDownList}
                            style={{ maxHeight: menuLayout.maxHeight }}
                        >
                            {options.map((option, idx) => (
                                <li
                                    style={{
                                        backgroundColor: option === selectedOption ? 'rgba(182, 106, 0, .25)' : null,
                                        color: disabled ? '#A5A5A5' : '',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                    }}
                                    className={classes.listItem}
                                    onClick={disabled ? null : onOptionClicked(option)}
                                    onMouseEnter={disabled ? showDisabledTooltip : undefined}
                                    onMouseLeave={disabled ? hideDisabledTooltip : undefined}
                                    key={`${option}-${idx}`}
                                >
                                    <span style={{ padding: '0', fontSize: '13px', maxWidth: '100%', display: 'inline-block' }}>
                                        <span style={{ fontFamily: "'Roboto', sans-serif" }}>{option}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>,
                    document.body
                )}
            {disabled && disabledTooltip && pageContent?.register_to_change_lang && createPortal(
                <div
                    ref={tooltipRootRef}
                    data-app-select-tooltip="true"
                    className={classes.floatingSelectTooltip}
                    style={{
                        top: disabledTooltip.top,
                        left: disabledTooltip.left,
                        maxWidth: disabledTooltip.maxWidth,
                    }}
                    dangerouslySetInnerHTML={createMarkup(pageContent.register_to_change_lang)}
                />,
                document.body
            )}
        </>
    );
};

export default Select;
